
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Grid, List, ArrowDownAZ, ArrowUpAZ, ArrowDownWideNarrow, ArrowUpWideNarrow, Hash, Lock, LockOpen } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect, useState } from "react";

interface FilterBarProps {
  location: string;
  type: string;
  sortBy: string;
  viewMode: 'grid' | 'list';
  locations: string[];
  types: string[];
  onLocationChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onViewModeChange: (mode: 'grid' | 'list') => void;
}

export function FilterBar({
  type,
  sortBy,
  viewMode,
  types,
  onTypeChange,
  onSortChange,
  onViewModeChange,
}: FilterBarProps) {
  const isMobile = useIsMobile();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const auth = localStorage.getItem("isAuthenticated") === "true";
      setIsAuthenticated(auth);
    };

    checkAuth();
    window.addEventListener("storage", checkAuth);

    return () => {
      window.removeEventListener("storage", checkAuth);
    };
  }, []);

  const handleSortClick = (field: 'name' | 'price' | 'quantity') => {
    const currentField = sortBy.split('-')[0];
    const currentDirection = sortBy.split('-')[1];
    
    if (currentField === field) {
      onSortChange(`${field}-${currentDirection === 'asc' ? 'desc' : 'asc'}`);
    } else {
      onSortChange(`${field}-asc`);
    }
  };

  const getSortIcon = (field: 'name' | 'price' | 'quantity') => {
    const [currentField, direction] = sortBy.split('-');
    if (currentField !== field) return null;
    
    switch (field) {
      case 'name':
        return direction === 'asc' ? <ArrowDownAZ className="h-4 w-4" /> : <ArrowUpAZ className="h-4 w-4" />;
      case 'price':
      case 'quantity':
        return direction === 'asc' ? <ArrowDownWideNarrow className="h-4 w-4" /> : <ArrowUpWideNarrow className="h-4 w-4" />;
    }
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {!isMobile && (
        <Select value={type} onValueChange={onTypeChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type">
              {type === "all-types" ? "All Types" : type}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {types.map((t) => (
              <SelectItem key={t} value={t}>
                {t === "all-types" ? "All Types" : t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="icon"
          onClick={() => handleSortClick('name')}
          className="relative"
        >
          {getSortIcon('name') || <ArrowDownAZ className="h-4 w-4" />}
        </Button>
        
        <Button
          variant="outline"
          size="icon"
          onClick={() => handleSortClick('price')}
          className="relative"
        >
          {getSortIcon('price') || <Hash className="h-4 w-4" />}
        </Button>
        
        <Button
          variant="outline"
          size="icon"
          onClick={() => handleSortClick('quantity')}
          className="relative"
        >
          {getSortIcon('quantity') || <ArrowDownWideNarrow className="h-4 w-4" />}
        </Button>
      </div>
      
      <div className="flex gap-1">
        <Button
          variant={viewMode === 'grid' ? 'default' : 'outline'}
          size="icon"
          onClick={() => onViewModeChange('grid')}
        >
          <Grid className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'outline'}
          size="icon"
          onClick={() => onViewModeChange('list')}
        >
          <List className="h-4 w-4" />
        </Button>
      </div>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link 
              to="/admin" 
              className="ml-4 inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-accent transition-colors"
            >
              {isAuthenticated ? (
                <LockOpen className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Lock className="h-5 w-5 text-muted-foreground" />
              )}
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            <p>Área Administrativa</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
